import React from 'react'
import { Grid } from '@mui/material'
import DiscountOutlinedIcon from '@mui/icons-material/DiscountOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { Units } from '../../../../../../redux/global/globalTypes'
import { EvolSellOutNumber, IncrementalNumber, KpiIcon, NumberCard, SecondaryNumberCard } from '../../../../../../components/features/NumberCard';
import DoughnutChart from '../../../../../../components/features/foodService/dashboard/graph/DoughnutChart';
import { Impact, PromotionsKpis, Performance, Cost, UnityType, PromoWarehouse, Assortment } from '../../../../../../redux/promotions/promotionsTypes';
import { StockageComponent } from './StockageComponent';
import { KpiType } from '../../../../../../components/features/PrimaryKpiCard';
import { ProductsList } from './ProductsList';
import { DrillDownType } from '../../../../../../components/features/drillDowns/ProductsDrillDownContent';
import { listOfFSDistributors } from '../../../types';
import { WarehousesList } from './WarehousesList';
import InventoryIcon from '@mui/icons-material/Inventory'
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople'
import { MenuType } from '../../../../../../components/features/filters/utils';
import { TooltipsKPIs } from '../../Dashboard/KPIsUtils';
import { KpiCardData } from '../../../../../../components/features/Kpis';

export interface StockageChart {
    sellOut: number,
    sellInGlobal: number,
    sellInPromo: number
}

export const KPIsConfig = (
    KPIs: PromotionsKpis,
    unit: Units
): KpiCardData[] => [
        {
            id: 1,
            type: KpiType.NUMBER,
            name: 'Volume sell-in promo',
            icon: <DiscountOutlinedIcon color='primary' />,
            units: unit,
            description: TooltipsKPIs.VOLUME_SELL_IN_PROMO,
            number: Math.round(KPIs.volumePromo),
            difference: Math.round(KPIs.differencePromo),
            evolution: KPIs.evolutionPromo,
            evolutionUnit: Units.percent,
        },
        {
            id: 2,
            type: KpiType.NUMBER,
            name: 'Volume sell-in global',
            icon: <LoginOutlinedIcon color='primary' />,
            units: unit,
            description: TooltipsKPIs.VOLUME_SELL_IN_GLOBAL,
            number: Math.round(KPIs.volumeSellIn),
            difference: Math.round(KPIs.differenceSellIn),
            evolution: KPIs.evolutionSellIn,
            evolutionUnit: Units.percent,
        },
        {
            id: 3,
            type: KpiType.NUMBER,
            name: 'Volume sell-out',
            icon: <LogoutOutlinedIcon color='primary' />,
            units: unit,
            description: TooltipsKPIs.VOLUME_SELL_OUT,
            number: Math.round(KPIs.volumeSellOut),
            difference: Math.round(KPIs.differenceSellOut),
            evolution: KPIs.evolutionSellOut,
            evolutionUnit: Units.percent,
        },
        {
            id: 4,
            type: KpiType.GAUGE,
            name: 'Budget',
            units: Units.euro,
            description: TooltipsKPIs.BUDGET,
            number: Math.round(KPIs.budgetTotal),
            difference: Math.round(KPIs.budgetInvest),
            evolution: Math.round(KPIs.percentageBudget)
        },
    ]

export const impactCardConfig = (impact: Impact, unit: Units) => [
    {
        id: 1,
        title: "Poids promo",
        subtitle: "Sell-in en promo sur sell-in global",
        component: <NumberCard number={impact.promoWeight} difference={impact.differencePromoWeight} unit={Units.percent} icon={KpiIcon.PROMO} evolutionUnit={Units.percent} />,
        flex: "0 0 20%",
    },
    {
        id: 2,
        title: "Stockage",
        subtitle: "Suivi de l'effet d'aubaine générant des stocks en dépôt.",
        component: <StockageComponent unit={unit} chart={impact.chart} labelGlobal={`Sell-in global (${unit})`} labelPart={`Sell-in promo (${unit})`} labelSecondBar={`Sell-out (${unit})`} stockagePercentage={impact.stockagePercentage} globalTendency={impact.globalTendency} />,
        flex: "1 1",
    },
    {
        id: 3,
        title: "Contribution à la croissance",
        subtitle: "Impact sur les volumes en sell-out.",
        component: <Grid container direction="row" width="100%" wrap="nowrap" > <IncrementalNumber number={impact.incremental} unit={unit} text='Incrémental sell-out' tooltip="La promotion n’a pas généré de ventes incrémentales." /> <EvolSellOutNumber number={impact.differenceSellOut} difference={impact.evolSellOut} unit={unit} text='Evo sell-out A-1' /> </ Grid>,
        flex: "1 1 100%",
    },

]

export const performanceCardConfig = (performance: Performance, unit: Units) => {
    const unitBasket = performance.unityType === UnityType.UF ? `${unit} / UF / Mois` : `${unit} / transaction`

    return [
        {
            id: 1,
            title: "Répartition d'utilisateurs finaux (UF)",
            subtitle: "Répartition des sell-out par typologie d’utilisateur final.",
            component: <Grid mb="2vh">
                <DoughnutChart />
            </Grid>,
            flex: "1 1 100%",
        },
        ...(performance.basket ? [{
            id: 3,
            title: "Evolution du panier moyen mensuel",
            subtitle: "Poids du panier moyen mensuel en sortie entrepôt.",
            component: <NumberCard number={performance.basket} difference={performance.differenceBasket} evol={performance.evolBasket} unit={unitBasket} icon={KpiIcon.VOLUME} kpi evolutionUnit={Units.percent} />,
            flex: "1 0",
        }] : []),
        ...(performance.UF ? (performance.unityType === UnityType.UF ? [{
            id: 2,
            title: "Evolution nombre d’utilisateurs finaux (UF)",
            subtitle: "Evolution de la distribution numérique (DN).",
            component: <NumberCard number={performance.UF} difference={performance.differenceUF} evol={performance.evolUF} unit={Units.UF} icon={KpiIcon.UF} kpi evolutionUnit={Units.percent} />,
            flex: "1 0",
        }] :
            [{
                id: 2,
                title: "Evolution nombre de transactions",
                subtitle: "Evolution de la distribution numérique (DN).",
                component: <NumberCard number={performance.UF} difference={performance.differenceUF} evol={performance.evolUF} unit={Units.transactions} icon={KpiIcon.UF} kpi evolutionUnit={Units.percent} />,
                flex: "1 0",
            }]) : []),
    ]
}

export const costCardConfig = (cost: Cost, unit: Units) => {
    return [
        {
            id: 1,
            title: "Coût d’acquisition du volume incrémental",
            subtitle: `Investissement en € par ${unit} sell-out incrémental distribué.`,
            component: <SecondaryNumberCard number={cost.CACVA} difference={cost.differenceInvestPerKg} evol={cost.evolInvestPerKg} unit={`€ / ${unit}`} icon={KpiIcon.VOLUME} tooltip={"L'incrémental étant négatif, nous ne pouvons pas calculer le coût d'acquisition du volume d'affaires incrémental."} />,
            flex: "1 0",
        },
        cost.CACUF ? cost.unityType === UnityType.UF ? {
            id: 2,
            title: "Coût d'acquisition d'un utilisateur final (UF)",
            subtitle: "Investissement € par nouvel UF acheteur.",
            component: <SecondaryNumberCard number={cost.CACUF} difference={cost.differenceCACUF} evol={cost.evolCACUF} unit={Units.euroPerUF} icon={KpiIcon.UF} tooltip={"L'évolution du nombre d'utilisateur final étant négatif, nous ne pouvons pas calculer le coût d'acquisition d'un nouvel utilisateur final."} />,
            flex: "1 1 ",
        } : {
            id: 2,
            title: "Coût d'acquisition d'une transaction",
            subtitle: "Investissement € par nouvelle transaction.",
            component: <SecondaryNumberCard number={cost.CACUF} difference={cost.differenceCACUF} evol={cost.evolCACUF} unit={Units["€PerTransaction"]} icon={KpiIcon.UF} tooltip={"L'évolution du nombre de transaction étant négatif, nous ne pouvons pas calculer le coût d'acquisition d'une nouvelle transaction."} />,
            flex: "1 1 ",
        } : {}
        ,]
}

export const dataGridConfig = (assortment: Assortment, warehouses: PromoWarehouse[], unit: Units) => {
    return [{
        id: "1",
        label: "Assortiments",
        title: " Quelles sont les références et assortissements les plus et les moins promophiles ?",
        children: <ProductsList
            unit={unit}
            productsList={assortment.productsList}
            drillDownType={DrillDownType.PROMO}
        />
    },
    {
        id: "2",
        label: "Dépôts",
        title: "Quelles sont les dépôts qui ont le mieux performé pendant votre période promotionnelle ?",
        children: <WarehousesList
            unit={unit}
            warehouses={warehouses}
            drillDownType={DrillDownType.WAREHOUSES}
        />
    }
    ]
}


export const getPromoFiltersConfig = (envName: string) => {
    switch (envName) {
        case listOfFSDistributors.METRO:
            return [
                {
                    id: 0,
                    name: 'products',
                    icon: <InventoryIcon />,
                    placeholder: 'Références',
                    type: MenuType.PRODUCTS,
                },
                {
                    id: 2,
                    name: 'regions',
                    icon: <EmojiPeopleIcon />,
                    placeholder: 'Dépôts',
                    type: MenuType.REGION,
                },
            ]
        default:
            return [
                {
                    id: 0,
                    name: 'products',
                    icon: <InventoryIcon />,
                    placeholder: 'Références',
                    type: MenuType.PRODUCTS,
                },
                {
                    id: 2,
                    name: 'clients',
                    icon: <EmojiPeopleIcon />,
                    placeholder: 'Types de clients',
                    type: MenuType.CLIENTS,
                },
            ]
    }
}